<template>
  <el-dialog
    :title="rateCard.exists ? rateCard.name : 'New Rate Card'"
    visible
    width="80vw"
    top="5vh"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    @close="$emit('update:isShow', false)"
  >
    <el-form
      ref="ratecardForm"
      v-loading="isSaving"
      :rules="rules"
      :model="rateCard"
      size="small"
    >
      <el-row :gutter="30">
        <el-col :md="12">
          <h1>
            General<br>
            <small>
              Description of general updates you're making
            </small>
          </h1>

          <el-row :gutter="10">
            <el-col :md="16">
              <el-form-item
                label="Name"
                prop="name"
              >
                <el-input
                  v-model="rateCard.name"
                  placeholder="Name"
                  clearable
                />
              </el-form-item>
            </el-col>
            <el-col :md="8">
              <el-form-item
                label="Type"
                prop="type"
              >
                <el-select
                  v-model="rateCard.type"
                  placeholder="Type"
                  class="w-100"
                >
                  <el-option
                    label="Buy"
                    value="buy"
                  />
                  <el-option
                    label="Sell"
                    value="sell"
                  />
                  <el-option
                    label="New Business"
                    value="new business"
                  />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="10">
            <el-col>
              <el-form-item
                label="Carrier"
                prop="carrier"
              >
                <el-select
                  v-model="rateCard.carrier"
                  filterable
                  remote
                  placeholder="Enter name of carrier"
                  class="w-100"
                  value-key="id"
                  :remote-method="remoteMethodCarrier"
                  :loading="isLoadingCarriers"
                  :disabled="!!rateCard.id"
                >
                  <el-option
                    v-for="item in carriers"
                    :key="item.id"
                    :label="item.name"
                    :value="item"
                  />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row>
            <el-col>
              <el-form-item
                label="Printing Template"
                prop="printing_template"
              >
                <el-select
                  v-model="rateCard.printing_template"
                  placeholder="Select a Printing Template"
                  class="w-100"
                >
                  <el-option
                    label="Default"
                    :value="'default'"
                  />
                  <el-option
                    label="Breaks"
                    :value="'breaks'"
                  />
                  <el-option
                    label="Simple"
                    :value="'simple'"
                  />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row>
            <el-col>
              <el-form-item
                label="Charge Account"
                prop="charge_account"
              >
                <el-input
                  v-model="rateCard.charge_account"
                  placeholder="Charge Account"
                  clearable
                />
              </el-form-item>
            </el-col>
          </el-row>
        </el-col>
        <el-col :md="12">
          <h1>
            Dates<br>
            <small>
              Modify the dates and describe the implications
            </small>
          </h1>
          <el-row>
            <el-col>
              <el-form-item
                label="Start"
                prop="date_start"
              >
                <el-date-picker
                  v-model="rateCard.date_start"
                  type="date"
                  format="dd/MM/yyyy"
                  value-format="yyyy-MM-dd"
                  placeholder="Pick a Start Date"
                  :picker-options="datePickerOption"
                  class="w-100"
                  :disabled="!rateCard.id"
                />
              </el-form-item>
            </el-col>
          </el-row>
          <el-row>
            <el-col>
              <el-form-item
                label="End"
                prop="date_end"
              >
                <el-date-picker
                  v-model="rateCard.date_end"
                  type="date"
                  format="dd/MM/yyyy"
                  value-format="yyyy-MM-dd"
                  placeholder="Pick a End Date"
                  :picker-options="datePickerOption"
                  class="w-100"
                  :disabled="!rateCard.id"
                />
              </el-form-item>
            </el-col>
          </el-row>
          <el-divider />
          <el-row>
            <el-col>
              <h1>
                Active<br>
                <small>
                  This is a description of the implications of changing the active field + dates.<br>For example setting to active will automatically attempt to end the previous Rate Card (if only one is found).
                </small>
              </h1>
              <el-row>
                <el-col>
                  <el-form-item
                    label="Active"
                    prop="active"
                  >
                    <el-select
                      v-model="rateCard.active"
                      placeholder="Active"
                      class="w-100"
                      :disabled="!rateCard.id"
                    >
                      <el-option
                        label="Yes"
                        :value="true"
                      />
                      <el-option
                        label="No"
                        :value="false"
                      />
                    </el-select>
                  </el-form-item>
                </el-col>
              </el-row>
            </el-col>
          </el-row>
        </el-col>
      </el-row>
    </el-form>
    <span
      slot="footer"
      class="dialog-footer"
    >
      <el-button @click.native="$emit('update:isShow', false)">
        Cancel
      </el-button>

      <el-button
        type="primary"
        :loading="isSaving"
        @click.native="rateCard.exists ? updateRateCard() : createRateCard()"
      >
        {{ rateCard.exists ? 'Save' : 'Create' }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
export default {
  props: {
    isShow: {
      type: Boolean,
      required: true
    },
    rateCard: {
      type: Object,
      required: false,
      default: () => {
        return new this.$sdk.ratecard()
      }
    }
  },
  data () {
    return {
      isSaving: false,
      isFullscreen: false,
      isLoadingCarriers: false,
      carriers: [],
      datePickerOption: {
        shortcuts: [{
          text: 'Today',
          onClick (picker) {
            picker.$emit('pick', new Date())
          }
        }, {
          text: 'Yesterday',
          onClick (picker) {
            const date = new Date()
            date.setTime(date.getTime() - 3600 * 1000 * 24)
            picker.$emit('pick', date)
          }
        }, {
          text: 'A week ago',
          onClick (picker) {
            const date = new Date()
            date.setTime(date.getTime() - 3600 * 1000 * 24 * 7)
            picker.$emit('pick', date)
          }
        }]
      },
      rules: {
        name: [
          { required: true, message: 'Name of the Rate Card is required', trigger: ['blur', 'change'] },
          { min: 5, max: 45, message: 'Length should be 5 to 45', trigger: ['blur', 'change'] }
        ],
        type: [
          { required: true, message: 'Type of the Rate Card is required', trigger: ['blur', 'change'] }
        ],
        carrier: [
          { required: true, message: 'Carrier of the Rate Card is required', trigger: 'change' }
        ],
        charge_account: [
          { required: true, message: 'Charge Account of the Rate Card is required', trigger: ['blur', 'change'] },
          { min: 3, max: 45, message: 'Length should be 3 to 45', trigger: ['blur', 'change'] }
        ]
      }
    }
  },
  created () {
    if (this.rateCard.carrier) {
      this.remoteMethodCarrier(this.rateCard.carrier.name)
    }
  },
  methods: {
    createRateCard () {
      this.$refs.ratecardForm.validate((valid) => {
        if (valid) {
          this.isSaving = true
          this.rateCard.group = new this.$sdk.ratecard_group()
          this.rateCard.create()
            .then(() => this.$message({ message: 'Successfully saved', type: 'success' }))
            .finally(() => {
              this.isSaving = false
              this.$emit('saved')
              this.$emit('update:isShow', false)
            })
        }
      })
    },
    updateRateCard () {
      this.$refs.ratecardForm.validate((valid) => {
        if (valid) {
          this.isSaving = true
          if (this.rateCard.date_start) {
            this.rateCard.date_start = this.$moment(this.rateCard.date_start).set({ hour: 0, minute: 0, second: 0 }).toDate()
          }
          if (this.rateCard.date_end) {
            this.rateCard.date_end = this.$moment(this.rateCard.date_end).set({ hour: 23, minute: 59, second: 59 }).toDate()
          }
          this.rateCard.update()
            .then(() => this.$message({ message: 'Successfully saved', type: 'success' }))
            .finally(() => {
              this.isSaving = false
              this.$emit('saved')
              this.$emit('update:isShow', false)
            })
        }
      })
    },
    remoteMethodCarrier (query) {
      this.isLoadingCarriers = true
      this.$sdk.carrier.list(builder => {
        builder.filters = { 'carrier_name': query }
      }).then(res => {
        this.carriers = res
      }).finally(() => { this.isLoadingCarriers = false })
    }
  }
}
</script>

<style>

</style>
